/*
 * A widget names its numbers as measures and computes everything else from them.
 *
 *     measures: {
 *         sales: {
 *             format: 'currency',
 *             sources: [
 *                 { entity: 'invoice',    aggregation: 'byPeriod', params: { state: 'final' }, value: 'net' },
 *                 { entity: 'creditNote', aggregation: 'byPeriod', params: { state: 'final' }, value: 'net', sign: -1 },
 *             ]
 *         }
 *     }
 *
 * A source is one aggregate read (GET /{entity}/agr/{aggregation}) and the
 * column of it to take. Its rows are summed into the measure, each times
 * `sign`; `match` keeps only the rows whose columns hold the given values.
 * A row with a `bucket` lands on the time axis; `group` names the column that
 * splits it into categories and `name` the column that labels them. Both can be
 * set on the measure for all its sources.
 *
 * Everything that shows a number takes an expression over the measures:
 *
 *     'sales'                            the measure, summed over the period
 *     { avg: 'sales' }                   per bucket: avg, max, min, last, sum
 *     { divide: ['sales', 'invoices'] }  also subtract, add, multiply
 *     { percentOf: ['paid', 'sales'] }   a / b × 100
 *     { shareOf: 'sales' }               a group's part of the measure's total, in %
 *     42                                 a constant
 *
 * The same expression is evaluated once for the whole period and once per
 * bucket or group, which is how a series and the total above it never disagree.
 */

export const sourceKey = ({ entity, aggregation, params = {} }) => {
    const qs = Object.keys(params).sort().map(k => `${k}=${params[k]}`).join('&')
    return `${entity}/${aggregation}${qs ? `?${qs}` : ''}`
}

// Every distinct read the measures need, once each
export const collectSources = (measures = {}) => {
    const seen = new Map()
    for (const measure of Object.values(measures)) {
        for (const source of measure.sources ?? []) {
            const key = sourceKey(source)
            if (!seen.has(key)) seen.set(key, source)
        }
    }
    return [...seen.entries()].map(([key, source]) => ({ key, ...source }))
}

// Postgres numeric arrives as a string when it is too wide for a JSON number
const num = (v) => {
    if (v == null || v === '') return 0
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
}

const matches = (row, match) => !match || Object.entries(match).every(([k, v]) => String(row[k]) === String(v))

/*
 * One measure read out of the rows of a range:
 *   total          the sum of every row
 *   byBucket       Map bucket → value
 *   byGroup        Map group → { key, name, value }
 *   byBucketGroup  Map bucket → Map group → value
 */
export const indexMeasure = (measure, rowsBySource) => {
    const index = { total: 0, byBucket: new Map(), byGroup: new Map(), byBucketGroup: new Map(), hasRows: false }
    for (const source of measure.sources ?? []) {
        const rows = rowsBySource[sourceKey(source)] ?? []
        const sign = source.sign ?? 1
        const groupCol = source.group ?? measure.group
        const nameCol = source.name ?? measure.name
        for (const row of rows) {
            if (!matches(row, source.match)) continue
            index.hasRows = true
            const value = num(row[source.value]) * sign
            index.total += value
            if (row.bucket) index.byBucket.set(row.bucket, (index.byBucket.get(row.bucket) ?? 0) + value)
            if (groupCol) {
                // A row with no group is a group of its own — "uncategorised" is an answer
                const key = row[groupCol] ?? ''
                const entry = index.byGroup.get(key) ?? { key, name: null, value: 0 }
                entry.value += value
                if (!entry.name && nameCol && row[nameCol]) entry.name = row[nameCol]
                index.byGroup.set(key, entry)
                if (row.bucket) {
                    const groups = index.byBucketGroup.get(row.bucket) ?? new Map()
                    groups.set(key, (groups.get(key) ?? 0) + value)
                    index.byBucketGroup.set(row.bucket, groups)
                }
            }
        }
    }
    return index
}

const reduceBuckets = (values, fn) => {
    if (!values.length) return null
    switch (fn) {
        case 'avg':  return values.reduce((s, v) => s + v, 0) / values.length
        case 'max':  return Math.max(...values)
        case 'min':  return Math.min(...values)
        case 'last': return values[values.length - 1]
        default:     return values.reduce((s, v) => s + v, 0)
    }
}

const BUCKET_FNS = ['avg', 'max', 'min', 'last', 'sum']

/*
 * `scope(id, fn)` answers what a measure is worth where the expression is
 * evaluated — over the period, in one bucket, for one group. `null` is
 * "cannot be computed" (a division by zero) and is shown as such, never as 0.
 */
export const evaluate = (expr, scope) => {
    if (expr == null) return null
    if (typeof expr === 'number') return expr
    if (typeof expr === 'string') return scope(expr, 'sum')
    const [op, arg] = Object.entries(expr)[0] ?? []
    if (BUCKET_FNS.includes(op)) return scope(arg, op)
    if (op === 'shareOf') return scope(arg, 'share')
    const values = (Array.isArray(arg) ? arg : [arg]).map(a => evaluate(a, scope))
    const [a, b] = values
    switch (op) {
        case 'add':       return values.reduce((s, v) => s + (v ?? 0), 0)
        case 'subtract':  return a == null ? null : a - (b ?? 0)
        case 'multiply':  return a == null || b == null ? null : a * b
        case 'divide':    return a == null || !b ? null : a / b
        case 'percentOf': return a == null || !b ? null : (a / b) * 100
        default:          return null
    }
}

// The measures an expression reads — to find the format it should be shown in
export const measuresOf = (expr) => {
    if (expr == null || typeof expr === 'number') return []
    if (typeof expr === 'string') return [expr]
    const [, arg] = Object.entries(expr)[0] ?? []
    return (Array.isArray(arg) ? arg : [arg]).flatMap(measuresOf)
}

/*
 * The measures of one range, resolved, with the three ways to read them. Built
 * once per fetch; every element of the widget reads from it.
 */
export const resolveMeasures = (measures = {}, rowsBySource, keys = []) => {
    const indexes = Object.fromEntries(Object.entries(measures).map(([id, m]) => [id, indexMeasure(m, rowsBySource)]))
    const timeline = (id) => keys.map(k => indexes[id]?.byBucket.get(k) ?? 0)

    const total = (expr) => evaluate(expr, (id, fn) => {
        const index = indexes[id]
        if (!index) return null
        if (fn === 'sum' || fn === 'share') return index.total
        // A measure read without buckets has only its total to give
        return index.byBucket.size ? reduceBuckets(timeline(id), fn) : index.total
    })

    const series = (expr) => keys.map(k => evaluate(expr, (id) => indexes[id]?.byBucket.get(k) ?? 0))

    const forGroup = (expr, groupKey) => evaluate(expr, (id, fn) => {
        const index = indexes[id]
        if (!index) return null
        const value = index.byGroup.get(groupKey)?.value ?? 0
        if (fn === 'share') return index.total ? (value / index.total) * 100 : null
        return value
    })

    return {
        keys,
        indexes,
        hasData: Object.values(indexes).some(i => i.hasRows),
        total,
        series,
        forGroup,
        groups: (id) => [...(indexes[id]?.byGroup.values() ?? [])],
        bucketGroups: (id, key) => indexes[id]?.byBucketGroup.get(key) ?? new Map(),
    }
}

/*
 * The categories of a grouped measure, largest first, cut to `limit` with the
 * rest summed into one "Other". Ties are broken by key so the order — and with
 * it each category's colour — does not shuffle between two equal periods.
 */
export const rankGroups = (groups, { limit, other = true, order = 'desc' } = {}) => {
    const sorted = [...groups].sort((a, b) =>
        (order === 'asc' ? a.value - b.value : b.value - a.value) || String(a.key).localeCompare(String(b.key)))
    if (!limit || sorted.length <= limit) return sorted
    const top = sorted.slice(0, other ? limit - 1 : limit)
    if (!other) return top
    const rest = sorted.slice(top.length)
    return [...top, { key: '__other', name: null, value: rest.reduce((s, g) => s + g.value, 0), other: true, count: rest.length }]
}

/*
 * The change between two values. `percent` is relative ((a − b) / |b|); `points`
 * is the plain difference, for values that are themselves percentages — a rate
 * going from 3 % to 5 % rose two points, not sixty-seven percent. No baseline
 * gives `isNew` rather than an infinite rise.
 */
export const change = (current, previous, mode = 'percent') => {
    if (current == null || previous == null) return null
    if (mode === 'points') return { value: current - previous, mode }
    if (previous === 0) return current === 0 ? { value: 0, mode } : { value: null, mode, isNew: true }
    return { value: ((current - previous) / Math.abs(previous)) * 100, mode }
}

// 'good' / 'bad' / 'neutral' for a change, given which way is favourable
export const tone = (delta, favorable = 'up') => {
    if (!delta || delta.isNew || delta.value == null || Math.abs(delta.value) < 0.05 || favorable === 'neutral') return 'neutral'
    const up = delta.value > 0
    return (favorable === 'down' ? !up : up) ? 'good' : 'bad'
}
